import * as React from "react";
import { useParams, Link } from "react-router-dom";
import { useCurrentStudyConfigurationContext } from "../../../Core/CurrentStudyConfigurationContext";
import LoadingWithMessage from "../../../misc/components/LoadingWithMessage";
import { Form, useFormApi, useFormState } from "informedv4";
import Alert, { AlertCategoryEnum } from "../../../misc/components/Alert";
import { CoughSettingsEvents } from "./CoughSettingsEvents";
import { CoughEventTypeItemState } from "./CoughEventType";
import { AuditForm, AuditModal } from "../../../auditmodal/components/AuditModal";
import { ActionButtons } from "../../../misc/ActionButtons";
import { useCookieRedirect } from "../../../misc/CookieRedirect";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../../../misc/api/WebPortalTokenClient";
import { useReferenceContextProvider } from "../../../Core/ReferenceContextProvider";
import { v4 } from "uuid";

interface CoughSettingsScreenFormState {
    eventTypes: CoughEventTypeItemState[];
    refEventTypeId?: string;
}

interface CoughSettingsScreenFormProps {
    studyId: string;
}

export const DeviceButtonOptions = [{
    Text: "None",
    Value: 0
}, {
    Text: "Button 1",
    Value: 1
}, {
    Text: "Button 2",
    Value: 2
}, {
    Text: "Button 3",
    Value: 3
}, {
    Text: "Button 4",
    Value: 4
}, {
    Text: "Mute",
    Value: 5,
}]

const StandardEventTypes: CoughEventTypeItemState[] = [
    { id: v4(), order: 11, name: "Mute", description: "Pressing the Mute button disables recording on the microphone and sensor channels during the time that the Mute button is depressed", period: true, color: "#8a8a8a", hidden: false, counted: true, deviceButton: { Text: "Mute", Value: 5 }},
    { id: v4(), order: 12, name: "Removal Subject", description: "", period: true, color: "#0000ff", hidden: false, counted: false, deviceButton: undefined },
    { id: v4(), order: 13, name: "Removal Clinic", description: "", period: true, color: "#ff00ff", hidden: false, counted: false, deviceButton: undefined },
    { id: v4(), order: 14, name: "Early Termination", description: "", period: false, color: "#ff8000", hidden: false, counted: false, deviceButton: undefined },
    { id: v4(), order: 15, name: "Early Stoppage", description: "", period: false, color: "#ffff00", hidden: false, counted: false, deviceButton: undefined },
    { id: v4(), order: 16, name: "Late Attachment", description: "", period: false, color: "#8000ff", hidden: false, counted: false, deviceButton: undefined },
    { id: v4(), order: 17, name: "QA Duration Event", description: "Used for marking QA durations", period: false, color: "#8b4513", hidden: false, counted: false, deviceButton: undefined },
]

export const CoughSettingsScreenForm = ({ studyId }: CoughSettingsScreenFormProps) => {
    const formApi = useFormApi();
    const formState = useFormState();
    const [alertShown, setAlertShown] = React.useState(false);
    const [errorAlert, setErrorAlert] = React.useState<string>(null);
    const [submitting, setSubmitting] = React.useState(false);
    const [auditModalVisible, setAuditModalVisible] = React.useState(false);
    const { refreshSettings } = useCurrentStudyConfigurationContext();
    const legacyClient = useWebPortalLegacyClient();
    const client = useWebPortalApiClient();

    const onPreAuditSubmit = () => {
        setAlertShown(false);
        setErrorAlert(null);
        setSubmitting(true);
        formApi.validate();

        const state = formApi.getFormState();

        if (state.invalid) {
            setSubmitting(false);
            return setErrorAlert("Errors exist on the page");
        }

        setSubmitting(false);
        setAuditModalVisible(true);
    }

    const onSubmit = async (auditForm: AuditForm) => {
        setSubmitting(true);
        setAuditModalVisible(false);
        setErrorAlert(null);

        formApi.validate();
        const state = formApi.getFormState();

        if (state.invalid) {
            setSubmitting(false);
            window.scrollTo(0, 0);
            return setErrorAlert("Errors exist on the page");
        }
        
        try {

            if (state.values.eventTypes == undefined)
                state.values.eventTypes = [];

            const deviceButtonEvents = [
                (state.values.eventTypes as CoughEventTypeItemState[]).some(et => et.deviceButton?.Value === 1),
                (state.values.eventTypes as CoughEventTypeItemState[]).some(et => et.deviceButton?.Value === 2),
                (state.values.eventTypes as CoughEventTypeItemState[]).some(et => et.deviceButton?.Value === 3),
                (state.values.eventTypes as CoughEventTypeItemState[]).some(et => et.deviceButton?.Value === 4),
                (state.values.eventTypes as CoughEventTypeItemState[]).some(et => et.deviceButton?.Value === 5),
            ];

            await legacyClient.patchSetting("cough.vitaloJakButtonsEnabled", deviceButtonEvents, studyId, auditForm.Annotation);

            const currentEventTypes = await client.client.getStudyCoughEventTypes(undefined, `studyId eq ${studyId}`);

            const duplicates = [...new Set((state.values.eventTypes as CoughEventTypeItemState[]).map(e => e.name)
                .filter((name, index, arr) => arr.indexOf(name) !== index))];

            if (duplicates.length > 0) {
                setErrorAlert(`You cannot have more than one event type with the name of '${duplicates}'.`);
                return;
            }

            for (const eventType of (state.values.eventTypes as CoughEventTypeItemState[])) {
                const eventTypeModel = {
                    id: eventType.id,
                    studyId: studyId,
                    name: eventType.name,
                    colour: eventType.color,
                    description: eventType.description,
                    order: eventType.order,
                    hidden: eventType.hidden,
                    counted: eventType.counted,
                    period: eventType.period,
                    deviceButton: eventType.deviceButton?.Value,
                    refEventTypeId: eventType.refEventTypeId,
                };

                if (currentEventTypes.value.find(cet => cet.id === eventType.id)) {
                    await client.client.patchStudyCoughEventType(eventTypeModel, eventType.id, auditForm.Annotation);
                } else {
                    await client.client.postStudyCoughEventType(eventTypeModel);
                }
            }

            const toRemove = currentEventTypes.value.filter(et => !(state.values.eventTypes as CoughEventTypeItemState[]).map(e => e.id).includes(et.id));

            for (const eventType of toRemove) {
                await client.client.deleteStudyCoughEventType(eventType.id);
            }

            setAlertShown(true);
            refreshSettings();
        } catch (ex) {
            setErrorAlert(ex.message);
        }

        window.scrollTo(0, 0)
        setSubmitting(false);
    }

    return (
        <>
            <Alert show={alertShown} category={AlertCategoryEnum.Success}>
                Saved successfully. Return to{" "}
                <Link to={`/studies`}>Studies list</Link>?
            </Alert>
            <Alert show={errorAlert !== null} category={AlertCategoryEnum.Danger}>
                {errorAlert}
            </Alert>           
            <CoughSettingsEvents />
            <div className="row">
                <div className="col-md-12">
                    <ActionButtons submitting={submitting} onSaveClick={() => onPreAuditSubmit()} automationIdPrefix="ActionsHeader" returnToListRoute="/studies" />
                </div>
            </div>
            {auditModalVisible ? (
                <AuditModal
                    header="Approval"
                    isSubmitting={submitting}
                    onClose={() => setAuditModalVisible(false)}
                    onSubmit={(audit) => onSubmit(audit)}
                />
            ) : null}
        </>
    );
}

export const CoughSettings = () => {
    const { id } = useParams<{ id: string }>();
    const [initialValues, setInitialValues] = React.useState<CoughSettingsScreenFormState>();
    const client = useWebPortalApiClient();
    useCookieRedirect(id);

    React.useEffect(() => {
        const getSettings = async () => {

            let eventTypeResult: CoughEventTypeItemState[] = (await client.client.getStudyCoughEventTypes(undefined, `studyId eq ${id}`, "order asc")).value.map(e => ({
                id: e.id,
                name: e.name,
                order: e.order,
                description: e.description,
                color: e.colour,
                period: e.period,
                hidden: e.hidden,
                counted: e.counted,
                deviceButton: DeviceButtonOptions.find(d => d.Value === e.deviceButton),
                refEventTypeId: e.refEventTypeId
            }));

            if (eventTypeResult.length === 0) {
                const coughEventTypesResult = await client.client.getCoughEventTypes();
                const mappedServerEvents: CoughEventTypeItemState[] = coughEventTypesResult.value.map(x => ({
                    id: v4(), order: 0, name: x.name, description: x.description, period: x.isPeriod, color: x.defaultColor, hidden: false, counted: true, deviceButton: undefined, refEventTypeId: x.id,
                }));

                eventTypeResult = [...mappedServerEvents, ...StandardEventTypes];
            }

            setInitialValues({
                eventTypes: eventTypeResult,
            });
        }

        getSettings();
    }, [client.client, id]);

    if (!initialValues) {
        return <LoadingWithMessage message="Loading Cough Events Settings..." />;
    }

    return (
        <Form validateOn="submit" noValidate initialValues={initialValues as unknown as Record<string, unknown>}>
            <CoughSettingsScreenForm studyId={id}  />
        </Form>
    );
}
